import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/VendorJobs.css';

interface Job {
  _id: string;
  serviceType: string;
  status: string;
  customer: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  driver?: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
    vehicleType: string;
    vehiclePlate: string;
  };
  estimatedPrice: number;
  createdAt: string;
  pickupLocation?: any;
  dropoffLocation?: any;
}

interface Driver {
  _id: string;
  firstName: string;
  lastName: string;
  isOnline: boolean;
  currentJobId?: string;
}

type TabType = 'pending' | 'active' | 'in-progress' | 'completed';

const VendorJobs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>((searchParams.get('status') as TabType) || 'pending');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigningJobId, setAssigningJobId] = useState<string | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');

  useEffect(() => {
    fetchJobs();
  }, [activeTab]);

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      let data;
      switch (activeTab) {
        case 'pending':
          data = await api.getVendorPendingJobs();
          setJobs(data.jobs || []);
          break;
        case 'active':
          data = await api.getVendorActiveJobs();
          setJobs(data.jobs || []);
          break;
        case 'in-progress':
          data = await api.getVendorInProgressJobs();
          setJobs(data.jobs || []);
          break;
        case 'completed':
          data = await api.getVendorCompletedJobs({ limit: 50 });
          setJobs(data.jobs || []);
          break;
      }
      setError('');
    } catch (err: any) {
      setError('Failed to load jobs');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const data = await api.getVendorFleetDrivers();
      setDrivers(data.drivers || []);
    } catch (err: any) {
      console.error('Failed to load drivers:', err);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ status: tab });
  };

  const openAssignModal = (jobId: string) => {
    setAssigningJobId(jobId);
    setSelectedDriverId('');
  };

  const closeAssignModal = () => {
    setAssigningJobId(null);
    setSelectedDriverId('');
  };

  const handleAssignJob = async () => {
    if (!assigningJobId || !selectedDriverId) {
      alert('Please select a driver');
      return;
    }

    try {
      await api.assignJobToVendorDriver(assigningJobId, selectedDriverId);
      closeAssignModal();
      fetchJobs();
      fetchDrivers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to assign job');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatPrice = (price: number) => {
    return `₹${price.toFixed(2)}`;
  };

  const getJobForAssignment = () => {
    return jobs.find(j => j._id === assigningJobId);
  };

  const availableDrivers = drivers.filter(d => d.isOnline && !d.currentJobId);

  return (
    <div className="vendor-jobs-page">
      <div className="page-header">
        <div>
          <h1>Jobs</h1>
          <p>Manage and assign jobs to your fleet</p>
        </div>
        <button className="refresh-button" onClick={fetchJobs}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => handleTabChange('pending')}
        >
          📋 Pending
        </button>
        <button
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => handleTabChange('active')}
        >
          ✅ Active
        </button>
        <button
          className={`tab ${activeTab === 'in-progress' ? 'active' : ''}`}
          onClick={() => handleTabChange('in-progress')}
        >
          🚗 In Progress
        </button>
        <button
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => handleTabChange('completed')}
        >
          ✔️ Completed
        </button>
      </div>

      {isLoading ? (
        <div className="loading">Loading jobs...</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="desktop-table-container">
            <div className="table-container">
              <table className="jobs-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Customer</th>
                    <th>Driver</th>
                    <th>Price</th>
                    <th>Created</th>
                    {activeTab === 'pending' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job._id}>
                      <td>
                        <strong>{job.serviceType.replace(/_/g, ' ')}</strong>
                      </td>
                      <td>
                        <div>{job.customer.firstName} {job.customer.lastName}</div>
                        <div className="secondary-text">{job.customer.phone}</div>
                      </td>
                      <td>
                        {job.driver ? (
                          <>
                            <div>{job.driver.firstName} {job.driver.lastName}</div>
                            <div className="secondary-text">
                              {job.driver.vehicleType} - {job.driver.vehiclePlate}
                            </div>
                          </>
                        ) : (
                          <span className="not-assigned">Not assigned</span>
                        )}
                      </td>
                      <td>{formatPrice(job.estimatedPrice)}</td>
                      <td>{formatDate(job.createdAt)}</td>
                      {activeTab === 'pending' && (
                        <td>
                          <button
                            onClick={() => openAssignModal(job._id)}
                            className="btn-assign"
                          >
                            👤 Assign Driver
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {jobs.length === 0 && (
                <div className="empty-state">
                  <p>No {activeTab} jobs</p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="mobile-card-list">
            {jobs.map((job) => (
              <div key={job._id} className="data-card">
                <div className="data-card-header">
                  <div>
                    <div className="data-card-title">
                      {job.serviceType.replace(/_/g, ' ')}
                    </div>
                    <div className="data-card-subtitle">
                      {job.customer.firstName} {job.customer.lastName}
                    </div>
                  </div>
                  <div className="job-price">{formatPrice(job.estimatedPrice)}</div>
                </div>

                <div className="data-card-body">
                  <div className="data-card-row">
                    <span className="data-card-label">Customer Phone:</span>
                    <span className="data-card-value">{job.customer.phone}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Driver:</span>
                    <span className="data-card-value">
                      {job.driver ? `${job.driver.firstName} ${job.driver.lastName}` : 'Not assigned'}
                    </span>
                  </div>
                  {job.driver && (
                    <>
                      <div className="data-card-row">
                        <span className="data-card-label">Vehicle:</span>
                        <span className="data-card-value">
                          {job.driver.vehicleType} - {job.driver.vehiclePlate}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="data-card-row">
                    <span className="data-card-label">Created:</span>
                    <span className="data-card-value">{formatDate(job.createdAt)}</span>
                  </div>
                </div>

                {activeTab === 'pending' && (
                  <div className="data-card-actions">
                    <button
                      onClick={() => openAssignModal(job._id)}
                      className="btn-assign-full"
                    >
                      👤 Assign Driver
                    </button>
                  </div>
                )}
              </div>
            ))}

            {jobs.length === 0 && (
              <div className="empty-state">
                <p>No {activeTab} jobs</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Assign Driver Modal */}
      {assigningJobId && (
        <div className="modal-overlay" onClick={closeAssignModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Driver to Job</h2>
              <button onClick={closeAssignModal} className="modal-close">✕</button>
            </div>

            <div className="modal-body">
              <div className="job-info">
                <h3>Job Details</h3>
                <p><strong>Service:</strong> {getJobForAssignment()?.serviceType.replace(/_/g, ' ')}</p>
                <p><strong>Customer:</strong> {getJobForAssignment()?.customer.firstName} {getJobForAssignment()?.customer.lastName}</p>
                <p><strong>Price:</strong> {formatPrice(getJobForAssignment()?.estimatedPrice || 0)}</p>
              </div>

              <div className="driver-select">
                <h3>Select Driver</h3>
                {availableDrivers.length > 0 ? (
                  <div className="driver-list">
                    {availableDrivers.map((driver) => (
                      <label key={driver._id} className="driver-option">
                        <input
                          type="radio"
                          name="driver"
                          value={driver._id}
                          checked={selectedDriverId === driver._id}
                          onChange={(e) => setSelectedDriverId(e.target.value)}
                        />
                        <span className="driver-name">
                          {driver.firstName} {driver.lastName}
                          <span className="driver-status">🟢 Online & Available</span>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="no-drivers">No online and available drivers. Please check your fleet.</p>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={closeAssignModal} className="btn-cancel">
                  Cancel
                </button>
                <button
                  onClick={handleAssignJob}
                  className="btn-confirm"
                  disabled={!selectedDriverId}
                >
                  Assign Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorJobs;
